import React, { createContext, useEffect, useContext, useState } from "react";
import axiosInstance from "../Config/axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Loader from "../Components/Loader/Loader";

export const ParentContext = createContext();

export const useParent = () => useContext(ParentContext);

export const ParentProvider = ({ children, user, setUser }) => {
  const [parentProfile, setParentProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // const time_Zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchLSParentDetails = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token || !user?.id) {
      return;
    }
    try {
      const response = await axiosInstance.get("/customer/parent-user-info/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        localStorage.removeItem("user");
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.detail || "Failed to fetch parent details.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      setParentProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentParent = async () => {
    const token = localStorage.getItem("accessToken");

    if (!token || !user?.id) {
      setParentProfile(null);
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get("/customer/parent-account/", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && typeof response.data === "object") {
        setParentProfile(response.data);
      } else {
        setParentProfile(null);
        navigate("/login");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.detail || "Failed to fetch parent",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      setParentProfile(null);
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const addParent = async (newData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/customer/parent-account/",
        newData,
      );
      setParentProfile(response.data);

      Swal.fire({
        icon: "success",
        title: "Account Created",
        text: "Parent account created successfully!",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      let loggedInUser = response?.data?.payload.user;
      if (loggedInUser?.user) {
        loggedInUser = { ...loggedInUser, ...loggedInUser.user };
      }

      localStorage.setItem("user", JSON.stringify(loggedInUser));
      localStorage.setItem("accessToken", response?.data?.payload.access);
      localStorage.setItem("refreshToken", response?.data?.payload.refresh);
      setUser(loggedInUser);
      // fetchCartCount();

      Swal.fire({
        icon: "success",
        title: response?.data?.message || "Signup successful",
        text: `Welcome , ${loggedInUser.first_name || "User"}!`,
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      if (loggedInUser.is_self_pay === false) {
        if (
          loggedInUser.player_count === 0 &&
          loggedInUser.has_preferences === false
        ) {
          if (
            loggedInUser.is_self_pay === false &&
            loggedInUser.has_payment_method === false
          ) {
            navigate("/add-card-self-dir");
          } else {
            navigate("/add-child-form");
          }
        } else if (loggedInUser.has_preferences === false) {
          navigate("/preferences");
        } else {
          navigate("/");
        }
      } else {
        navigate("/buy-packages");
      }

      return { success: true, data: response.data };
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.detail || "Failed to create account.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const updateParentProfile = async (updatedData) => {
    const token = localStorage.getItem("accessToken");

    if (!token || !user?.id) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized",
        text: "You must be logged in to update the profile.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false };
    }

    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        "/customer/parent-account/",
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              updatedData instanceof FormData
                ? "multipart/form-data"
                : "application/json",
          },
          // params: { timezone: time_Zone },
        },
      );

      setParentProfile(response.data);

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Profile updated successfully.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      return { success: true, data: response.data };
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err.response?.data?.detail || "Failed to update profile.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const changePasswordParentProfile = async (data) => {
    const token = localStorage.getItem("accessToken");

    if (!token || !user?.id) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized",
        text: "You must be logged in to update the profile.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false };
    }

    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        "/customer/change-password/",
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Profile updated successfully.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      return { success: true, data: response.data };
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err.response?.data?.detail || "Failed to update password.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const addParentExisting = async (newData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/customer/activate-bwc/",
        newData,
      );
      setParentProfile(response.data);

      Swal.fire({
        icon: "success",
        title: "Account Created",
        text: "Parent account created successfully!",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      let loggedInUser = response?.data?.payload.user;
      if (loggedInUser?.user) {
        loggedInUser = { ...loggedInUser, ...loggedInUser.user };
      }

      localStorage.setItem("user", JSON.stringify(loggedInUser));
      localStorage.setItem("accessToken", response?.data?.payload.access);
      localStorage.setItem("refreshToken", response?.data?.payload.refresh);
      setUser(loggedInUser);
      // fetchCartCount();

      Swal.fire({
        icon: "success",
        title: response?.data?.message || "Signup successful",
        text: `Welcome , ${loggedInUser.first_name || "User"}!`,
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      if (loggedInUser.is_self_pay === false) {
        if (loggedInUser.has_payment_method === false) {
          if (
            loggedInUser.is_self_pay === false &&
            loggedInUser.has_payment_method === false
          ) {
            navigate("/add-card-self-dir");
          } else {
            navigate("/add-child-form");
          }
        } else if (loggedInUser.has_preferences === false) {
          navigate("/preferences");
        } else {
          navigate("/");
        }
      } else {
        navigate("/buy-packages");
      }

      return { success: true, data: response.data };
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.message || "Failed to create account.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });

      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const token = localStorage.getItem("accessToken");
  //   if (user?.id && token) {
  //     fetchCurrentParent();
  //   } else {
  //     setParentProfile(null);
  //   }
  // }, [user?.id, location.pathname]);

  return (
    <ParentContext.Provider
      value={{
        parentProfile,
        loading,
        fetchLSParentDetails,
        fetchCurrentParent,
        updateParentProfile,
        addParent,
        addParentExisting,
      }}
    >
      {loading && <Loader />}
      {children}
    </ParentContext.Provider>
  );
};
